import type {ButtonSize, HeaderDataPayload} from '@ds/types';
import type {ReactNode} from 'react';

import {ArrowRightOutlined, SearchOutlined} from '@ant-design/icons';
import {Badge, Button, IconButton, Modal} from '@ds/components';
import {cn} from '@ds/helpers';
import {NavBar} from '@ds/layout';
import {useState} from 'react';
import {To, useNavigate} from 'react-router-dom';

export type LayoutHeaderProps = {
  title?: string;
  modalTitle?: string;
  modalText?: string;
  hasBack?: boolean;
  link?: string | number;
  linkWithConfirmation?: boolean;
  hasLeftIcon?: boolean;
  leftIconLink?: string;
  headerData?: HeaderDataPayload;
  extra?: ReactNode;
};

const searchIcon = <SearchOutlined aria-hidden />;

const resolveTrailingIconButtonSize = (
  hasLeftIcon: boolean | undefined,
  headerData?: HeaderDataPayload
): ButtonSize => headerData?.iconButtonSize ?? (hasLeftIcon ? 'lg' : 'sm');

const LayoutHeader = ({
  title,
  modalTitle,
  modalText,
  hasBack,
  link,
  linkWithConfirmation = false,
  hasLeftIcon,
  leftIconLink,
  headerData,
  extra
}: LayoutHeaderProps) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const navigateToLink = () => {
    if (link === null || link === undefined || link === '') return;
    navigate(link as To);
  };

  const handleBack = () => {
    if (!linkWithConfirmation) {
      navigateToLink();
      return;
    }
    setConfirmOpen(true);
  };

  const trailingIcon = headerData?.icon ?? (hasLeftIcon ? searchIcon : undefined);
  const hasHeaderIcon = trailingIcon != null;
  const showTrailing = !extra && (headerData?.component || hasHeaderIcon);
  const trailingHandler = headerData?.action
    ? headerData.action
    : leftIconLink
      ? () => navigate(leftIconLink)
      : undefined;

  const leading = hasBack ? (
    <IconButton aria-label="بازگشت" size="sm" disableHover onClick={handleBack}>
      <ArrowRightOutlined aria-hidden />
    </IconButton>
  ) : undefined;

  const trailing =
    showTrailing && headerData?.component ? (
      <div
        role={headerData.action ? 'button' : undefined}
        tabIndex={headerData.action ? 0 : undefined}
        onClick={headerData.action ?? undefined}
        onKeyDown={
          headerData.action
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  headerData.action?.();
                }
              }
            : undefined
        }
        className="flex items-center"
      >
        {headerData.component}
      </div>
    ) : showTrailing && trailingIcon ? (
      headerData?.iconTitle ? (
        <Button
          variant="ghost"
          color="secondary"
          size="sm"
          disableWave
          onClick={trailingHandler}
          className={cn(
            'relative gap-1 px-2 hover:bg-transparent! active:bg-transparent! hover:text-neutral-fg! focus:outline-none focus-visible:outline-none',
            'text-gray-07',
            headerData?.iconClassName
          )}
        >
          <span className="text-body-xs-emphasis text-gray-07">{headerData.iconTitle}</span>
          {trailingIcon}
          {(headerData?.badgeNumber ?? 0) > 0 ? (
            <Badge
              variant="count"
              count={headerData?.badgeNumber}
              className="absolute -top-1 start-0"
            />
          ) : null}
        </Button>
      ) : (
        <IconButton
          aria-label={hasLeftIcon ? 'جستجو' : 'اعلان‌ها'}
          size={resolveTrailingIconButtonSize(hasLeftIcon, headerData)}
          disableHover
          onClick={trailingHandler}
          className={cn('relative', headerData?.iconClassName ?? undefined)}
        >
          {trailingIcon}
          {(headerData?.badgeNumber ?? 0) > 0 ? (
            <Badge
              variant="count"
              count={headerData?.badgeNumber}
              className="absolute -top-1 start-0"
            />
          ) : null}
        </IconButton>
      )
    ) : undefined;

  return (
    <>
      <NavBar
        title={headerData?.title || title}
        leading={leading}
        extra={extra}
        trailing={trailing}
      />
      <Modal
        defaultHeader
        show={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={modalTitle}
        footer={
          <div className="flex w-full items-center justify-between border-t border-gray-03 p-4">
            <Button variant="link" color="secondary" onClick={() => setConfirmOpen(false)}>
              انصراف
            </Button>
            <Button
              variant="fill"
              color="destructive"
              onClick={() => {
                navigateToLink();
                setConfirmOpen(false);
              }}
            >
              خروج
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <p>{modalText}</p>
        </div>
      </Modal>
    </>
  );
};

export default LayoutHeader;
